<template>
  <b-card title="eKiosk KPI Dashboard">
    <b-row class="mb-3">
      <b-col md="3">
        <label class="d-block">From</label>
        <b-form-input v-model="fromDate" type="date" />
      </b-col>
      <b-col md="3">
        <label class="d-block">To</label>
        <b-form-input v-model="toDate" type="date" />
      </b-col>
      <b-col md="4">
        <label class="d-block d-flex align-items-center">
          KPI Name
          <kpi-process-flow-help class="ml-50" />
        </label>
        <b-form-select v-model="kpiType" :options="kpiOptions" />
      </b-col>
      <b-col md="2" class="d-flex align-items-end">
        <b-button variant="primary" :disabled="loading" block @click="load">
          {{ loading ? 'Loading...' : 'Load' }}
        </b-button>
      </b-col>
    </b-row>

    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <div v-if="meta && summary">
      <h4 class="mb-2 all-counter-summary-title">All Counter Summary</h4>
      <div class="summary-card mb-3">
        <b-row>
          <b-col cols="6" md class="metric-cell summary-metric-cell">
            <p class="metric-label mb-1">Average</p>
            <p class="metric-value mb-0" :class="statusClassByTime(summary.avgTime)">
              {{ summary.avgTime }} min
            </p>
          </b-col>
          <b-col cols="6" md class="metric-cell summary-metric-cell">
            <p class="metric-label mb-1">Target KPI</p>
            <p class="metric-value mb-0">{{ meta.thresholdMinutes }} min</p>
          </b-col>
          <b-col cols="6" md class="metric-cell summary-metric-cell">
            <p class="metric-label mb-1">&gt; KPI</p>
            <p class="metric-value mb-0 text-danger">
              {{ summary.aboveKPI.count }} ({{ summary.aboveKPI.percentage }}%)
            </p>
          </b-col>
          <b-col cols="6" md class="metric-cell summary-metric-cell">
            <p class="metric-label mb-1">&lt;= KPI</p>
            <p class="metric-value mb-0 text-success">
              {{ summary.belowKPI.count }} ({{ summary.belowKPI.percentage }}%)
            </p>
          </b-col>
          <b-col cols="12" md class="metric-cell summary-metric-cell">
            <p class="metric-label mb-1">Total Orders</p>
            <p class="metric-value mb-0">{{ summary.totalOrders }}</p>
          </b-col>
        </b-row>
      </div>

      <h5 class="mb-2">By Counter</h5>
      <b-row v-if="perCounter.length > 0">
        <b-col
          v-for="counter in perCounter"
          :key="counter.counterId || counter.counterName"
          lg="4"
          md="6"
        >
          <b-card class="counter-card h-100">
            <div class="counter-card-header d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0 text-truncate counter-card-name" :title="counter.counterName">
                {{ counter.counterName || 'Unnamed Counter' }}
              </h6>
              <span class="kpi-param-chip flex-shrink-0 ml-2">{{ selectedKpiLabel }}</span>
            </div>
            <div class="metric-list">
              <div class="metric-item">
                <span class="metric-label">Average</span>
                <span class="metric-value" :class="statusClassByTime(counter.avgTime)">
                  {{ counter.avgTime }} min
                </span>
              </div>
              <div class="metric-item">
                <span class="metric-label">Target KPI</span>
                <span class="metric-value">
                  {{ meta.thresholdMinutes }} min
                </span>
              </div>
              <div class="metric-item">
                <span class="metric-label">&gt; KPI</span>
                <span class="metric-value text-danger">
                  {{ counter.aboveKPI.count }} ({{ counter.aboveKPI.percentage }}%)
                </span>
              </div>
              <div class="metric-item">
                <span class="metric-label">&lt;= KPI</span>
                <span class="metric-value text-success">
                  {{ counter.belowKPI.count }} ({{ counter.belowKPI.percentage }}%)
                </span>
              </div>
              <div class="metric-item">
                <span class="metric-label">Total Orders</span>
                <span class="metric-value">{{ counter.totalOrders }}</span>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <b-alert v-else show variant="light" class="mb-0">
        No counter data available for the selected filter.
      </b-alert>
    </div>
  </b-card>
</template>

<script>
import KpiProcessFlowHelp from './kpi-process-flow-help.vue';

function defaultDates() {
  const to = new Date();
  const from = new Date(to.getTime() - 7 * 86400000);
  return {
    fromDate: from.toISOString().slice(0, 10),
    toDate: to.toISOString().slice(0, 10),
  };
}

export default {
  components: {
    KpiProcessFlowHelp,
  },
  data() {
    const d = defaultDates();
    return {
      fromDate: d.fromDate,
      toDate: d.toDate,
      kpiType: 'QUEUE_TIME',
      summary: null,
      perCounter: [],
      meta: null,
      loading: false,
      error: '',
    };
  },
  computed: {
    kpiOptions() {
      return (this.$store.state.kioskKpi.masterItems || [])
        .filter((item) => item.isActive)
        .map((item) => ({ value: item.kpiName, text: item.displayName }));
    },
    selectedKpiLabel() {
      const type = this.meta && this.meta.kpiType;
      if (!type) return '';
      const found = this.kpiOptions.find((option) => option.value === type);
      return found ? found.text : type;
    },
  },
  async mounted() {
    document.title = 'eKiosk KPI Dashboard | RSP';
    await this.$store.dispatch('kioskKpi/fetchKpiMaster');
    this.load();
  },
  methods: {
    statusClassByTime(minutes) {
      return Number(minutes) > Number(this.meta && this.meta.thresholdMinutes)
        ? 'text-danger'
        : 'text-success';
    },
    async load() {
      this.error = '';
      this.loading = true;
      const fromIso = new Date(this.fromDate).toISOString();
      const toEnd = new Date(this.toDate);
      toEnd.setHours(23, 59, 59, 999);
      try {
        const result = await this.$store.dispatch('kioskKpi/fetchKpiDashboard', {
          from: fromIso,
          to: toEnd.toISOString(),
          kpiType: this.kpiType,
        });
        const payload = result.data || {};
        this.meta = {
          kpiType: payload.kpiType,
          thresholdMinutes: payload.thresholdMinutes,
        };
        this.summary = payload.allCounters;
        this.perCounter = payload.perCounter || [];
      } catch (err) {
        const data = err.response && err.response.data;
        this.error =
          (data && data.errors && data.errors[0] && data.errors[0].message) ||
          (data && data.message) ||
          err.message ||
          'Failed to load dashboard';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.all-counter-summary-title {
  font-weight: bold;
  color: #000;
  padding-left: 0.75rem;
  border-left: 8px solid #40aee5;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}

.summary-card {
  background-color: #f5f7fc;
  border: 1px solid #40aee5;
  border-left-width: 8px;
  border-radius: 0.428rem;
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

.counter-card {
  border: 1px solid #d9d9d9;
  border-top: 5px solid #40aee5;
  border-radius: 0.75rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background-color: #ffffff;
}

.counter-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.06), 0 4px 10px rgba(0, 0, 0, 0.03);
  border-color: #d9d9d9;
  border-top-color: #40aee5;
}

.counter-card-header {
  gap: 0.5rem;
}

.counter-card-name {
  font-weight: 600;
}

.kpi-param-chip {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  border-radius: 0.358rem;
  background-color: #edf3ff;
  color: #40aee5;
}

.metric-cell {
  margin-bottom: 8px;
}

.summary-card .summary-metric-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
  padding-left: 1rem;
  padding-right: 1rem;
  text-align: center;
}

.summary-card .summary-metric-cell:not(:last-child) {
  border-right: 1px solid #40aee5;
}

@media (min-width: 768px) {
  .summary-card .summary-metric-cell {
    margin-bottom: 0;
  }
}

@media (max-width: 767.98px) {
  .summary-card .summary-metric-cell:nth-child(2n) {
    border-right: none;
  }

  .summary-card .summary-metric-cell:nth-child(2n + 1):not(:last-child) {
    border-right: 1px solid #40aee5;
  }

}

.metric-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #dce3f0;
}

.metric-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.summary-card .metric-label {
  color: #495057;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.metric-label {
  color: #6c757d;
  font-size: 12px;
}

.summary-card .metric-value {
  font-size: 1.125rem;
}

.metric-value {
  font-weight: 600;
}
</style>
